import type { ComponentProps } from 'react';
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { InputField } from '#pie/components/input-field/InputField';
import type { InputTextAreaProps } from '#pie/components/input-text-area/InputTextArea';
import { InputTextArea } from '#pie/components/input-text-area/InputTextArea';
import { useExtendedRules } from '../formHelpers';

type FormTextProps<T extends FieldValues> = Omit<
  InputTextAreaProps,
  | 'id'
  | 'isError'
  | 'required'
  | 'minLength'
  | 'maxLength'
  | keyof ControllerRenderProps<T, Path<T>>
> &
  Pick<UseControllerProps<T>, 'disabled' | 'name' | 'rules' | 'defaultValue'> &
  Pick<ComponentProps<typeof InputField>, 'label' | 'className'>;

export function FormTextArea<T extends FieldValues>({
  name,
  disabled,
  rules: originalRules,
  defaultValue,
  label,
  ...props
}: FormTextProps<T>) {
  const rules = useExtendedRules(originalRules);
  const {
    fieldState: { error },
    field: { value, onChange, ...field },
  } = useController({
    defaultValue,
    disabled,
    name,
    rules,
  });

  return (
    <InputField
      name={name}
      isRequired={!!rules?.required}
      label={label}
      error={error?.message}
    >
      <InputTextArea
        id={name}
        isError={!!error}
        disabled={disabled}
        value={value || ''}
        minLength={
          typeof rules?.minLength === 'object'
            ? rules.minLength.value
            : undefined
        }
        maxLength={
          typeof rules?.maxLength === 'object'
            ? rules.maxLength.value
            : undefined
        }
        onChange={onChange}
        {...field}
        {...props}
      />
    </InputField>
  );
}
